import { formModes } from "shared/constants/allocationsEnums";
import { BestWorkGroups } from "shared/constants/appEnums";
import {
    Button as ButtonBootstrap,
    Container,
    FormGroup,
} from "react-bootstrap";
import { forwardRef, useContext, useImperativeHandle, useState } from "react";
import {
    useStylesForButton,
    useStylesForModal,
    useStylesForText,
} from "shared/styles/makeStyles";

import Button from "shared/components/inputs/mui/Button";
import Close from "shared/components/utils/icons/Close";
import Context from "features/allocations/contexts/Context";
import CustomGroupContext from "features/allocations/contexts/CustomGroupContext";
import EmployeeGridSelectionContext from "features/allocations/contexts/EmployeeGridSelectionContext";
import FilterContext from "features/allocations/contexts/FilterContext";
import IconButton from "shared/components/inputs/button/IconButton";
import Link from "shared/components/navigation/links/AnchorLink";
import Loader from "shared/components/feedback/loading/Loader";
import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import Text from "shared/components/data-display/mui/Text";
import TextAreaInput from "shared/components/inputs/text-input/TextAreaInput";
import TextInputWithValidation from "shared/components/inputs/text-input/TextInputWithValidation";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import { ValidationError } from "shared/constants/errorTypes";
import moment from "moment";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { useCustomGroups } from "features/allocations/api/useCustomGroups";
import { useForm } from "react-hook-form";
import { useValidationError } from "shared/hooks/errors/useValidationError";
import {
    ModalControl,
    Content,
    FormField,
    Action,
    LoaderWrapper,
} from "./CustomGroup.styles";

const CustomGroup = forwardRef((props, ref) => {

    const session = useContext(SessionContext);

    const isBestworkAdmin = session.groups.some(x => x.code === BestWorkGroups.BWAdmin);

    const {
        getCustomGroup,
        validateCustomGroup,
        createCustomGroup,
        updateCustomGroup,
        deleteCustomGroup,
    } = useCustomGroups();

    const { selected } = props;

    const {
        control,
        setError,
        trigger,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    });

    const [show, setShow] = useState(false);
    const [title, setTitle] = useState("");
    const [mode, setMode] = useState(formModes.ADD);
    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [createdBy, setCreatedBy] = useState("");
    const [created, setCreated] = useState("");
    const [updated, setUpdated] = useState("");
    const [own, setOwn] = useState(false);
    const [isBestworkAdminGroup, setIsBestworkAdminGroup] = useState(isBestworkAdmin);
    const [description, setDescription] = useState("");
    const classes = useStylesForModal();
    const filterConsumer = useContext(FilterContext);
    const buttonClasses = useStylesForButton();
    const classesText = useStylesForText();
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [updatedBy, setUpdatedBy] = useState("");

    const { handleFormErrorResponse } = useValidationError();
    const customGroupConsumer = useContext(CustomGroupContext);
    const employeeGridSelectionConsumer = useContext(
        EmployeeGridSelectionContext
    );

    const employeesConsumer = useContext(Context);

    useImperativeHandle(ref, () => ({
        add(employeeData) {
            setId("");
            setName("");
            setDescription("");
            setMode(formModes.ADD);
            setTitle("Create Custom Group");
            setShow(true);
            setSelectedUser(employeeData);
        },
        detail(id) {
            setId(id);
            handleLoad(id);
            setDetailsMode();
            setShow(true);
        },
        delete(id, name) {
            setId(id);
            setName(name);
            setMode(formModes.DELETE);
            setTitle("Delete Custom Group?");
            setShow(true);
        },
    }));

    const setInProgress = (saving, loaderId, buttonsId) => {
        let loader = document.getElementById(loaderId);
        let buttons = document.getElementById(buttonsId);

        if (saving && loader) {
            loader.style.display = "block";
        } else if (!saving && loader) {
            loader.style.display = "none";
        }

        if (saving && buttons) {
            buttons.style.display = "none";
        } else if (!saving && buttons) {
            buttons.style.display = "block";
        }
    };

    const handleClose = () => {
        setId("");
        setName("");
        setDescription("");
        setCreatedBy("");
        setCreated("");
        setUpdated("");
        setOwn(false);
        setIsBestworkAdminGroup(false);
        setMode(formModes.ADD);
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    const setDetailsMode = () => {
        setMode(formModes.DETAIL);
        setTitle("Custom Group Details");
    };

    const onCancel = () => {
        mode === formModes.UPDATE || mode === formModes.DELETE
            ? setDetailsMode()
            : handleClose();
    };

    const messageCreated = (customGroupId) => {
        const hasSelection =
            employeeGridSelectionConsumer.selectedEmployeeIDs.length > 0;

        const allSelected = employeeGridSelectionConsumer?.allSelected;

        const employeeNames =
            employeeGridSelectionConsumer.getNamesFromSelectedIDs();

        const listLength =
            employeeGridSelectionConsumer.selectedEmployeeIDs.length;

        return (
            <>
                You have created the Custom Group <b>{name}</b>
                {hasSelection ? (
                    <>
                        {", and added\u00a0"}
                        <b>
                            {employeeNames[0]}&nbsp;
                            {listLength > 1 && (
                                <>
                                    <span>and </span>

                                    {allSelected ? (
                                        "all filter result employees"
                                    ) : (
                                        <Tooltip
                                            placement="top"
                                            title={employeeNames
                                                .slice(1)
                                                .map((name, idx) => (
                                                    <Text
                                                        key={`${name}-${idx}`}
                                                        variant="xsmall"
                                                    >
                                                        {name}
                                                    </Text>
                                                ))}
                                        >
                                            <ButtonBootstrap
                                                variant="link"
                                                className={
                                                    buttonClasses.tooltipLink
                                                }
                                                style={{
                                                    color: globalThis.config.colors
                                                        .white,
                                                    fontSize:
                                                        globalThis.config.fonts
                                                            .sizes.small,
                                                }}
                                            >
                                                {`${listLength - 1} ${listLength > 2
                                                    ? "others"
                                                    : "other"
                                                    }`}
                                            </ButtonBootstrap>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            &nbsp;
                        </b>
                    </>
                ) : (
                    "."
                )}
                <Link
                    variant="secondary"
                    color={globalThis.config.colors.white}
                    href={void 0}
                    onClick={() => {
                        globalThis.location.href = `${globalThis.location.href.split("?")[0]
                            }?customGroupId=${customGroupId}`;
                    }}
                >
                    View Group
                </Link>
            </>
        );
    };
    const messageUpdated = () => {
        return (
            <>
                You have updated the Custom Group <b>{name}</b>.
            </>
        );
    };
    const messageDeleted = () => {
        return (
            <>
                The Custom Group <b>{name}</b> has been deleted.
            </>
        );
    };
    const handleLoad = async function (id) {
        setIsLoadingData(true);

        const payload = await getCustomGroup([{ name: "$id", value: id }]);
        setName(payload.Name);
        setDescription(payload.Description);
        setCreatedBy(payload.UserCreatorName);
        setUpdatedBy(payload.UpdatedByUserName);
        setCreated(moment(payload.CreationDateTime).format("MMMM D, YYYY"));
        setUpdated(
            moment(payload.LastUpdateDateTime).format("MMMM D, YYYY")
        );
        setOwn(payload.Own);
        setIsBestworkAdminGroup(isBestworkAdmin);
        setIsLoadingData(false);
    };

    const handleSave = async function () {
        switch (mode) {
            case formModes.ADD: {
                const resolvedId = selectedUser?.id ?? selectedUser?.userId;
                const userIdsFallback = selectedUser ? [resolvedId] : [];
                const userIds =
                    employeeGridSelectionConsumer.selectedEmployeeIDs.length > 0
                        ? employeeGridSelectionConsumer.selectedEmployeeIDs
                        : userIdsFallback;
                const allSelected = employeeGridSelectionConsumer?.allSelected
                    ? employeeGridSelectionConsumer.allSelected
                    : false;

                const query = {
                    name,
                };
                setInProgress(
                    true,
                    "divCustomGroup_loader",
                    "divCustomGroup_buttons"
                );
                try {
                    await validateCustomGroup(query);
                    try {
                        const customGroupId = await createCustomGroup(
                            name,
                            description,
                            userIds,
                            allSelected,
                            filterConsumer.summaryQuery
                        );
                        sendNotification(
                            messageCreated(customGroupId)
                        );
                        setInProgress(
                            false,
                            "divCustomGroup_loader",
                            "divCustomGroup_buttons"
                        );
                        handleClose();
                        employeeGridSelectionConsumer.setSelectionIsEnabled(
                            false
                        );
                        employeeGridSelectionConsumer.deselectAllEmployeeIDs();
                    } catch (error) {
                        setInProgress(
                            false,
                            "divCustomGroup_loader",
                            "divCustomGroup_buttons"
                        );
                        handleFormErrorResponse(error, setError);
                    }
                } catch (error) {
                    setInProgress(false, "divCustomGroup_loader", "divCustomGroup_buttons");
                    const message = error.message;
                    const status = error.status;
                    const correlationId = error.correlationId;
                    const body = error.detail;
                    const verror = new ValidationError(message, status, correlationId, body);
                    handleFormErrorResponse(verror, setError);
                }
                break;
            }
            case formModes.UPDATE:
                setInProgress(
                    true,
                    "divCustomGroup_loader",
                    "divCustomGroup_buttons"
                );
                try {
                    await updateCustomGroup({
                        id: id,
                        name: name,
                        description: description,
                    });
                    if (Number.parseInt(selected?.Id) === Number.parseInt(id)) {
                        const selectedGroup = {
                            ...customGroupConsumer.selectedCustomGroup,
                        };
                        selectedGroup.Name = name;

                        customGroupConsumer.setSelectedCustomGroup(
                            selectedGroup
                        );
                    }
                    setInProgress(
                        false,
                        "divCustomGroup_loader",
                        "divCustomGroup_buttons"
                    );
                    handleClose();
                    sendNotification(messageUpdated());
                } catch (error) {
                    const message = error.message;
                    const status = error.status;
                    const correlationId = error.correlationId;
                    const body = error.detail;
                    const verror = new ValidationError(message, status, correlationId, body);
                    handleFormErrorResponse(verror, setError);
                    setInProgress(
                        false,
                        "divCustomGroup_loader",
                        "divCustomGroup_buttons"
                    );
                }
                break;
            default:
                break;
        }
    };
    const handleDelete = async function () {
        const loaderId = "divCustomGroup_loader_deleting";
        const buttonsId = "divCustomGroup_deleting_buttons";
        setInProgress(true, loaderId, buttonsId);
        try {
            await deleteCustomGroup([{ name: "$id", value: id }]);
            if (Number.parseInt(selected?.Id) === Number.parseInt(id)) {
                customGroupConsumer.setSelectedCustomGroup(null);
                employeesConsumer?.setEmployees([]);
                let uri = globalThis.location.toString();
                if (uri.indexOf("?") > 0) {
                    let clean_uri = uri.substring(0, uri.indexOf("?"));
                    globalThis.history.replaceState(null, null, clean_uri);
                }
            }
            setInProgress(true, loaderId, buttonsId);
            handleClose();
            sendNotification(messageDeleted());
        } catch (error) {
            setInProgress(true, loaderId, buttonsId);
            handleFormErrorResponse(error, setError);
        }
    };

    return (
        <>
            <ModalControl onClick={handleShow}>{props.children}</ModalControl>
            <Modal
                show={show}
                onHide={handleClose}
                backdrop={globalThis.config.modal.static}
                keyboard={globalThis.config.modal.keyboard}
                centered
                size="sm"
                className={classes.root}
                animation={false}
            >
                <Modal.Header>
                    <Modal.Title>
                        <Text variant="xlarge">{title}</Text>
                    </Modal.Title>
                    <IconButton
                        icon={<Close />}
                        onClick={() => handleClose()}
                    />
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        {isLoadingData ? (
                            <LoaderWrapper>
                                <Loader height="3rem" width="3rem" />
                            </LoaderWrapper>
                        ) : (
                            <Content>
                                {(mode === formModes.ADD ||
                                    mode === formModes.UPDATE) && (
                                        <>
                                            <FormGroup>
                                                <TextInputWithValidation
                                                    required={true}
                                                    label="Custom Group Name"
                                                    control={control}
                                                    name="name"
                                                    errors={errors}
                                                    onChange={(event) =>
                                                        setName(event.target.value)
                                                    }
                                                    value={name}
                                                    defaultValue={name}
                                                ></TextInputWithValidation>
                                            </FormGroup>
                                            <FormGroup>
                                                <TextAreaInput
                                                    label="Add a description (Optional)"
                                                    name="description"
                                                    onChange={(event) =>
                                                        setDescription(
                                                            event.target.value
                                                        )
                                                    }
                                                    rows={5}
                                                    cols={5}
                                                    value={description}
                                                />
                                            </FormGroup>
                                            <Action>
                                                {
                                                    <>
                                                        <div
                                                            id="divCustomGroup_loader"
                                                            style={{
                                                                display: "none",
                                                            }}
                                                        >
                                                            <Loader
                                                                height="2rem"
                                                                width="2rem"
                                                            />
                                                        </div>
                                                        <div id="divCustomGroup_buttons">
                                                            <Link
                                                                style={{
                                                                    paddingRight:
                                                                        "20px",
                                                                }}
                                                                variant="secondary"
                                                                onClick={onCancel}
                                                            >
                                                                Cancel
                                                            </Link>
                                                            <Button
                                                                onClick={async () => {
                                                                    const valid =
                                                                        await trigger();
                                                                    valid &&
                                                                        handleSave();
                                                                }}
                                                            >
                                                                {mode ===
                                                                    formModes.ADD && (
                                                                        <>Create</>
                                                                    )}
                                                                {mode ===
                                                                    formModes.UPDATE && (
                                                                        <>Update</>
                                                                    )}
                                                            </Button>
                                                        </div>
                                                    </>
                                                }
                                            </Action>
                                        </>
                                    )}
                                {mode === formModes.DETAIL && (
                                    <>
                                        <FormField>
                                            <Text
                                                variant="xsmall"
                                                className={classesText.gray2}
                                            >
                                                Custom Group Name
                                            </Text>
                                            <Text variant="medium">{name}</Text>
                                        </FormField>
                                        <FormField>
                                            <Text
                                                variant="xsmall"
                                                className={classesText.gray2}
                                            >
                                                Description
                                            </Text>
                                            <Text variant="medium">
                                                {description}
                                            </Text>
                                        </FormField>
                                        <FormField>
                                            <Text
                                                variant="xsmall"
                                                className={classesText.gray2}
                                            >
                                                Created By
                                            </Text>
                                            <Text variant="medium">
                                                {createdBy} - {created}
                                            </Text>
                                        </FormField>
                                        {updatedBy && (
                                            <FormField>
                                                <Text
                                                    variant="xsmall"
                                                    className={
                                                        classesText.gray2
                                                    }
                                                >
                                                    Last Updated
                                                </Text>
                                                <Text variant="medium">
                                                    {updatedBy} - {updated}
                                                </Text>
                                            </FormField>
                                        )}
                                        {(own || isBestworkAdminGroup) && (
                                            <Action>
                                                <Button
                                                    className={[
                                                        buttonClasses.button,
                                                        buttonClasses.secondary,
                                                        buttonClasses.noMargin,
                                                    ]}
                                                    onClick={() => {
                                                        setMode(
                                                            formModes.DELETE
                                                        );
                                                        setTitle(
                                                            "Delete Custom Group?"
                                                        );
                                                    }}
                                                >
                                                    Delete
                                                </Button>
                                                <Button
                                                    onClick={() => {
                                                        setMode(
                                                            formModes.UPDATE
                                                        );
                                                        setTitle(
                                                            "Edit Custom Group Details"
                                                        );
                                                    }}
                                                    className={[
                                                        buttonClasses.button,
                                                        buttonClasses.noMargin,
                                                        buttonClasses.primaryButton,
                                                    ]}
                                                >
                                                    Edit
                                                </Button>
                                            </Action>
                                        )}
                                    </>
                                )}
                                {mode === formModes.DELETE && (
                                    <>
                                        <FormField>
                                            <Text variant="medium">
                                                You are about to delete the
                                                Custom Group{" "}
                                                <strong>{name}</strong>.
                                                <br />
                                                <br />
                                                Do you wish to continue?
                                            </Text>
                                        </FormField>
                                        <Action>
                                            <div
                                                id="divCustomGroup_loader_deleting"
                                                style={{ display: "none" }}
                                            >
                                                <Loader
                                                    height="2rem"
                                                    width="2rem"
                                                />
                                            </div>
                                            <div id="divCustomGroup_deleting_buttons">
                                                <Link
                                                    variant="secondary"
                                                    onClick={onCancel}
                                                    style={{
                                                        paddingRight: "32px",
                                                    }}
                                                >
                                                    Cancel
                                                </Link>
                                                <Button onClick={handleDelete}>
                                                    Yes, Delete
                                                </Button>
                                            </div>
                                        </Action>
                                    </>
                                )}
                            </Content>
                        )}
                    </Container>
                </Modal.Body>
            </Modal>
        </>
    );
});

CustomGroup.propTypes = {
    selected: PropTypes.object,
    children: PropTypes.node,
};

export default CustomGroup;
